import { useEffect, useRef, useState } from 'react'
import { TURNSTILE_ENABLED, loadTurnstile } from '@/tools/submit'
import { TURNSTILE_SITE_KEY } from '@/lib/site'
import { CONTACT_FALLBACK_EMAIL } from '@/lib/contact'
import { useI18n } from '@/i18n/I18nProvider'

/**
 * The job application form — one per role page, posting to
 * `/api/job-application` on this origin, which the Worker relays (signed) to
 * Excellence's `website-job-application` function. The application lands in
 * People → Hiring; the CV lands in a private bucket. Asked for on 2026-09-07:
 * *"onlardan gelen başvurular bizim internal sayfamıza düşmeli"*.
 *
 * Deliberately NOT `ContactForm`. That form is a lead into the CRM: it wants a
 * company and it is the same on every page. This one wants a CV, a LinkedIn
 * profile and a consent that names recruitment, and its shape is the
 * candidate's rather than the page's. Sharing the component would have made
 * every field on both forms optional to satisfy the other.
 *
 * **Source.** A LinkedIn post we promote links to the role page with
 * `?src=linkedin`; the form reads that once on mount and sends it, so the
 * pipeline can tell a promoted-post applicant from an organic one without a
 * second landing page (Kagan: *"bazen linkedin job'ları promote ediyoruz"*).
 * Anything else, or nothing, is `careers_page`.
 *
 * **Consent.** A tick is required and its wording names the purpose and the
 * retention (twelve months) — this is KVKK data, a CV being the most personal
 * document a stranger hands a company. The date of the tick is recorded on the
 * candidate row.
 *
 * Written pages always hydrate (`prerender.tsx`), so unlike `ContactForm` there
 * is no inline-script twin: React's `handleSubmit` is the only path. The
 * `method="dialog"` fallback stays so a click before hydration does nothing
 * rather than reloading the page with the answers in the URL.
 */
export default function ApplyForm({ opening }: { opening: string }) {
  const { locale } = useI18n()
  const [sent, setSent] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [source, setSource] = useState<{ source: string; detail: string | null }>({
    source: 'careers_page',
    detail: null,
  })
  const widgetBox = useRef<HTMLDivElement>(null)
  const widgetId = useRef<string | undefined>(undefined)
  /** Turnstile errored or never loaded; cleared if a retry later succeeds. */
  const widgetFailed = useRef(false)
  const openedAt = useRef(Date.now()).current
  const idPrefix = `apply-${opening}-`

  useEffect(() => {
    const src = new URLSearchParams(location.search).get('src')
    if (src && /^[a-z0-9_-]{1,40}$/i.test(src)) {
      setSource({ source: src.toLowerCase() === 'linkedin' ? 'linkedin' : 'careers_page', detail: src })
    }
  }, [])

  useEffect(() => {
    if (!TURNSTILE_ENABLED) return
    let cancelled = false
    loadTurnstile()
      .then(() => {
        const el = widgetBox.current
        if (cancelled || !el || !window.turnstile || widgetId.current) return
        el.replaceChildren()
        widgetId.current = window.turnstile.render(el, {
          sitekey: TURNSTILE_SITE_KEY,
          // RETRY IS SAID OUT LOUD (2026-10-02). 'auto' is Turnstile's default,
          // so this changes nothing today; it is written so a later edit cannot
          // turn it off unnoticed — `verify-turnstile.mjs` requires it at every
          // render. Do NOT add a `reset()` to the error-callback as well: the
          // widget is already retrying, and a reset would restart it mid-retry.
          retry: 'auto',
          appearance: 'interaction-only',
          callback: () => {
            widgetFailed.current = false
          },
          'error-callback': (code?: string) => {
            widgetFailed.current = true
            console.error('[dp] turnstile refused to render:', code)
          },
        })
      })
      .catch((e) => {
        widgetFailed.current = true
        console.error('[dp] turnstile failed to load:', e)
      })
    return () => {
      cancelled = true
    }
  }, [])

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault()
    if (busy) return
    setError(null)
    const form = e.currentTarget
    if (!form.reportValidity()) return

    const data = new FormData(form)
    const cv = data.get('cv')
    if (!(cv instanceof File) || cv.size === 0) {
      setError('Please attach your CV.')
      return
    }
    if (cv.size > 10 * 1024 * 1024) {
      setError('The CV is over 10 MB. Please send a smaller file.')
      return
    }

    let token: string | undefined
    if (TURNSTILE_ENABLED) {
      if (widgetId.current) {
        try {
          token = window.turnstile?.getResponse(widgetId.current)
        } catch (e2) {
          console.error('[dp] turnstile getResponse threw:', e2)
        }
      }
      // No widget at all used to fall through and send without a token, which
      // the Worker refuses with a line about a check nobody could see. A widget
      // that errored is the same case (2026-10-02): say why, and give the one
      // address that receives mail.
      if (!token) {
        setError(
          widgetFailed.current || !widgetId.current
            ? locale === 'tr'
              ? `Güvenlik doğrulaması tarayıcınızda yüklenmedi. Sayfayı yenileyin ya da bu sitede reklam engelleyiciyi kapatın — veya başvurunuzu ${CONTACT_FALLBACK_EMAIL} adresine gönderin.`
              : `The security check did not load in your browser. Reload the page or turn off your ad blocker for this site — or send your application to ${CONTACT_FALLBACK_EMAIL}.`
            : 'Please complete the check above the button, then send again.',
        )
        return
      }
    }

    data.set('opening', opening)
    data.set('source', source.source)
    if (source.detail) data.set('source_detail', source.detail)
    data.set('locale', locale)
    data.set('elapsed_ms', String(Date.now() - openedAt))
    if (token) data.set('turnstile', token)

    setBusy(true)
    try {
      const res = await fetch('/api/job-application', { method: 'POST', body: data })
      if (res.ok) {
        setSent(true)
        return
      }
      const body = (await res.json().catch(() => ({}))) as { error?: string }
      setError(body.error ?? 'We could not send that just now. Please try again.')
    } catch {
      setError('We could not send that just now. Please try again.')
    } finally {
      setBusy(false)
    }
  }

  const control = 'mt-2 w-full rounded-md border border-border bg-card px-4 py-3 text-base'
  const label = 'block text-sm font-medium'

  if (sent) {
    return (
      <div className="rounded-lg border border-border bg-card/70 p-6 shadow-sm sm:p-8" role="status">
        <p className="text-lg leading-relaxed">
          Thank you — your application is in. We read every one, and we will write back to the address
          you gave us.
        </p>
        <p className="mt-4 text-base leading-relaxed text-muted-foreground">
          If you need to add anything, email{' '}
          <a href={`mailto:${CONTACT_FALLBACK_EMAIL}`} className="font-medium text-primary hover:underline">
            {CONTACT_FALLBACK_EMAIL}
          </a>
          .
        </p>
      </div>
    )
  }

  return (
    <div className="rounded-lg border border-border bg-card/70 p-6 shadow-sm sm:p-8">
      <form method="dialog" onSubmit={handleSubmit} noValidate={false} className="space-y-5">
        {/* The honeypot: a real form leaves it empty. Off-screen, not `hidden`,
            so a naive bot still sees a field to fill. */}
        <div className="absolute -left-[9999px] top-auto h-px w-px overflow-hidden" aria-hidden="true">
          <label htmlFor={`${idPrefix}website`}>Website</label>
          <input id={`${idPrefix}website`} name="website" type="text" tabIndex={-1} autoComplete="off" />
        </div>

        <div className="grid gap-5 sm:grid-cols-2">
          <div>
            <label htmlFor={`${idPrefix}full_name`} className={label}>
              Full name <span className="text-primary">*</span>
            </label>
            <input id={`${idPrefix}full_name`} name="full_name" type="text" required maxLength={200}
              autoComplete="name" className={control} />
          </div>
          <div>
            <label htmlFor={`${idPrefix}email`} className={label}>
              Email <span className="text-primary">*</span>
            </label>
            <input id={`${idPrefix}email`} name="email" type="email" required maxLength={200}
              autoComplete="email" className={control} />
          </div>
          <div>
            <label htmlFor={`${idPrefix}phone`} className={label}>Phone</label>
            <input id={`${idPrefix}phone`} name="phone" type="tel" maxLength={60} autoComplete="tel"
              className={control} />
          </div>
          <div>
            <label htmlFor={`${idPrefix}linkedin_url`} className={label}>LinkedIn profile</label>
            <input id={`${idPrefix}linkedin_url`} name="linkedin_url" type="url" maxLength={300}
              placeholder="https://linkedin.com/in/…" className={control} />
          </div>
          <div className="sm:col-span-2">
            <label htmlFor={`${idPrefix}location`} className={label}>Where are you based?</label>
            <input id={`${idPrefix}location`} name="location" type="text" maxLength={120}
              placeholder="Istanbul" className={control} />
          </div>
        </div>

        <div>
          <label htmlFor={`${idPrefix}cover_letter`} className={label}>
            Why this role, and what have you built?
          </label>
          <textarea id={`${idPrefix}cover_letter`} name="cover_letter" rows={6} maxLength={6000}
            placeholder="A few lines are enough. Links to things you have built are very welcome."
            className={control} />
        </div>

        <div>
          <label htmlFor={`${idPrefix}cv`} className={label}>
            CV <span className="text-primary">*</span>{' '}
            <span className="font-normal text-muted-foreground">PDF or Word, up to 10 MB</span>
          </label>
          <input id={`${idPrefix}cv`} name="cv" type="file" required
            accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
            className="mt-2 block w-full text-base file:mr-4 file:rounded-md file:border file:border-border file:bg-card file:px-4 file:py-2 file:text-sm file:font-medium hover:file:bg-accent" />
        </div>

        <label className="flex cursor-pointer items-start gap-3 text-sm leading-relaxed">
          <input name="consent" type="checkbox" required value="true" className="mt-1 h-4 w-4" />
          <span>
            I consent to Digitopia processing the personal data in this application and my CV for the
            purpose of recruitment, and keeping it for up to twelve months so I can be considered for
            this and similar roles. <span className="text-primary">*</span>
          </span>
        </label>

        <div ref={widgetBox} data-dp-turnstile={TURNSTILE_SITE_KEY} className="min-h-0" />

        {error && (
          <p role="alert" className="text-sm text-primary">
            {error}
          </p>
        )}

        <button type="submit" className="btn-primary" disabled={busy}>
          {busy ? 'Sending…' : 'Send application'}
        </button>
      </form>
      <p className="mt-4 text-sm text-muted-foreground">
        You will hear from us either way. Questions? Write to{' '}
        <a href={`mailto:${CONTACT_FALLBACK_EMAIL}`} className="font-medium text-primary hover:underline">
          {CONTACT_FALLBACK_EMAIL}
        </a>
        .
      </p>
    </div>
  )
}
